import { get, isEmpty, range } from 'lodash-es';
import { OFFER_TYPE_TABLE_CHART_POINTS } from 'site-modules/shared/components/car-buying/offer-type-table/offer-type-table-chart-data';
import { PURCHASE, LEASE } from 'site-modules/shared/constants/car-buying';
import { getDealsIncludingIncentives } from 'site-modules/shared/utils/price-checker';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getPrices } from 'site-modules/shared/utils/inventory-utils/get-prices';

const BOUNDARY_FACTOR = 0.01;
const { GREAT_CHART_POINTS_NUM, GOOD_CHART_POINTS_NUM, FAIR_CHART_POINTS_NUM, HIGH_CHART_POINTS_NUM } =
  OFFER_TYPE_TABLE_CHART_POINTS;

export function getSellingPrice({ dealItems, isPurchase }) {
  const vinData = dealItems?.[0]?.inventory || {};
  const { prices, computedDisplayInfo, type } = vinData;
  const { advertisedPrice, edmundsSuggestedPrice } = getPrices(prices, computedDisplayInfo, type);
  const isUsed = !isNew(type);
  let maxGoodPrice;
  if (!isEmpty(dealItems)) {
    const [, goodDeal] = getDealsIncludingIncentives({ dealItems, showLeasePayments: !isPurchase });
    maxGoodPrice = goodDeal?.dealOffer?.sellingPrice;
  }
  return isUsed ? advertisedPrice : advertisedPrice || maxGoodPrice || edmundsSuggestedPrice;
}

/**
 * Gets closest slider value index based on input value.
 * @param {number[]} sliderPayments
 * @param {number} inputValue
 * @returns {number}
 */
export const getSliderClosestValue = (sliderPayments, inputValue) => {
  let sliderValue = 0;

  sliderPayments.reduce((prev, curr, ind) => {
    if (Math.abs(curr - inputValue) < Math.abs(prev - inputValue)) {
      sliderValue = ind;
      return curr;
    }

    return prev;
  }, 0);

  return sliderValue;
};

/**
 * Gets range based on passed params.
 * @param {number} from
 * @param {number} to
 * @param {number} divider
 * @returns {number[]}
 */
export const getPaymentsRange = (from, to, divider) => {
  const r = range(from, to, (to - from) / divider);

  // removes last value since range doesn't add `to` value and append `to` value
  r.pop();
  r.push(to);

  return r.map(Math.round);
};

/**
 * Returns lower slider/graph payment.
 * @param {boolean} isUsed
 * @param {number} p1
 * @param {number} p2
 * @return {number}
 */
const getLowerPayment = (isUsed, p1, p2) =>
  isUsed && p1 <= p2 ? Math.round(p1) : Math.round(p2 - p2 * BOUNDARY_FACTOR);

/**
 * Returns high slider/chart payment.
 * @param {number} p1
 * @param {number} p2
 * @return {number}
 */
const getHighPayment = (p1, p2) => (p1 <= p2 ? p2 + p2 - p1 : p1 + p1 * BOUNDARY_FACTOR);

/**
 * Returns range for prices.
 * @param {Array} dealItems
 * @param {boolean} isUsed
 * @param {boolean} showLeasePayments
 * @return {{lower: number, upper: number, range: number[]}|null}
 */
const getPriceRange = ({ dealItems, isUsed, showLeasePayments }) => {
  const sellingPrice = getSellingPrice({ dealItems, isPurchase: !showLeasePayments });
  const [greatDeal, goodDeal, fairDeal] = getDealsIncludingIncentives({
    dealItems,
    showLeasePayments,
  });
  // calculate price for each type
  const great = greatDeal?.dealOffer?.sellingPrice;
  const good = goodDeal?.dealOffer?.sellingPrice;
  const fair = fairDeal?.dealOffer?.sellingPrice;
  // if there is no some price return null because we need them all to build price range
  if (!(great && good && fair)) return null;
  // set lower and upper price limits
  const lower = getLowerPayment(isUsed, sellingPrice, great);
  const upper = getHighPayment(fair, sellingPrice);
  // calculate range for each type
  return {
    range: [
      ...getPaymentsRange(lower, great, GREAT_CHART_POINTS_NUM),
      ...getPaymentsRange(great + 1, good, GOOD_CHART_POINTS_NUM),
      ...getPaymentsRange(good + 1, fair, FAIR_CHART_POINTS_NUM),
      ...getPaymentsRange(fair + 1, upper, HIGH_CHART_POINTS_NUM),
    ],
    lower,
    upper,
  };
};

/**
 * Returns range for monthly payments.
 * @param {object} dealItems
 * @param {boolean} isUsed
 * @param {boolean} useBase
 * @return {number[]}
 */
const getMonthlyRange = ({ dealItems, isUsed, useBase }) => {
  const paymentField = useBase ? 'baseMonthlyPayment' : 'monthlyPayment';
  const greatMonthlyPayment = Math.round(get(dealItems, `[0].dealOffer.${paymentField}`));
  const goodMonthlyPayment = Math.round(get(dealItems, `[1].dealOffer.${paymentField}`));
  const fairMonthlyPayment = Math.round(get(dealItems, `[2].dealOffer.${paymentField}`));
  const highMonthlyPayment = Math.round(
    get(dealItems, `[2].calculatedData.${useBase ? 'msrpBaseMonthlyPayment' : 'msrpMonthlyPayment'}`)
  );

  const lowerMonthlyPayment = getLowerPayment(isUsed, highMonthlyPayment, greatMonthlyPayment);
  const upperMonthlyPayment = getHighPayment(fairMonthlyPayment, highMonthlyPayment);

  return [
    ...getPaymentsRange(lowerMonthlyPayment, greatMonthlyPayment, GREAT_CHART_POINTS_NUM),
    ...getPaymentsRange(greatMonthlyPayment + 1, goodMonthlyPayment, GOOD_CHART_POINTS_NUM),
    ...getPaymentsRange(goodMonthlyPayment + 1, fairMonthlyPayment, FAIR_CHART_POINTS_NUM),
    ...getPaymentsRange(fairMonthlyPayment + 1, upperMonthlyPayment, HIGH_CHART_POINTS_NUM),
  ];
};

/**
 * Returns slider payments values.
 * @param {object} dealItems
 * @param {boolean} isUsed
 * @param {number} sellingPrice
 * @param {boolean} showLeasePayments
 * @param {boolean} useBase
 * @returns {{sliderValue: number, inputValue: number, lowerPayment: number, sliderPayments: number[], upperPayment: number}}
 */
export const getSliderPayments = ({
  dealItems,
  isUsed,
  sellingPrice = 0,
  showLeasePayments = false,
  useBase = false,
}) => {
  const purchaseDealItems = dealItems[PURCHASE];
  const leaseDealItems = dealItems[LEASE];

  const sliderPurchasePayments = getPriceRange({
    dealItems: purchaseDealItems,
    isUsed,
    showLeasePayments: false,
  });

  if (!sliderPurchasePayments) return null;

  const hasLeaseItems = !isEmpty(leaseDealItems);

  const { range: sliderPayments = [], upper: upperPayment = 0, lower: lowerPayment = 0 } = sliderPurchasePayments;
  const {
    range: sliderLeasePayments = [],
    upper: upperLeasePayment = 0,
    lower: lowerLeasePayment = 0,
  } = hasLeaseItems
    ? getPriceRange({
        dealItems: leaseDealItems,
        isUsed: false,
        showLeasePayments: true,
      })
    : {};
  const sliderLeaseMonthlyPayments = hasLeaseItems
    ? getMonthlyRange({ dealItems: leaseDealItems, isUsed: false, useBase })
    : [];

  return {
    lowerPayment,
    upperPayment,
    lowerLeasePayment,
    upperLeasePayment,
    inputValue: sellingPrice,
    sellingPrice,
    sliderValue: sellingPrice
      ? getSliderClosestValue(showLeasePayments ? sliderLeaseMonthlyPayments : sliderPayments, sellingPrice)
      : OFFER_TYPE_TABLE_CHART_POINTS.DATA_LENGTH,
    sliderPayments,
    sliderLeasePayments,
    sliderMonthlyPayments: getMonthlyRange({ dealItems: purchaseDealItems, isUsed, useBase }),
    sliderLeaseMonthlyPayments,
  };
};

/**
 * Returns slider payments data.
 * @param {object} vehicle
 * @return {object}
 */
export function getUsedSliderPaymentsData({ vehicle }) {
  const priceValidation = get(vehicle, 'computedDisplayInfo.priceValidation');
  const displayPrice = get(vehicle, 'prices.advertisedPrice');

  const maxGreatPrice = get(priceValidation, 'maxGreatPrice');
  const maxGoodPrice = get(priceValidation, 'maxGoodPrice');
  const maxFairPrice = get(priceValidation, 'maxFairPrice');

  // trying to get the closest value
  const prices = {
    maxGreatPrice: maxGreatPrice || maxGoodPrice || maxFairPrice || displayPrice,
    maxGoodPrice: maxGoodPrice || maxFairPrice || displayPrice,
    maxFairPrice: maxFairPrice || displayPrice,
  };

  return getSliderPayments({
    dealItems: {
      [PURCHASE]: [
        { inventory: vehicle, dealOffer: { sellingPrice: prices.maxGreatPrice } },
        { inventory: vehicle, dealOffer: { sellingPrice: prices.maxGoodPrice } },
        { inventory: vehicle, dealOffer: { sellingPrice: prices.maxFairPrice } },
      ],
    },
    isUsed: true,
  });
}
